import { Component, DoCheck, Input, OnInit } from '@angular/core';
import { TeamMemberAccessService } from 'src/app/api/team-member-access.service';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { SelectComponent as GroupsSelectPage } from 'src/app/components/groups/select/select.component';
import { CreatePage as ClientRolesCreate } from 'src/app/clients/roles/create/create.page';
import { SelectPage as ProjectSelectPage } from 'src/app/projects/select/select.page';
import { AddressBookComponent } from 'src/app/components/address-book/address-book.component';
import { TimeAndBillingService } from '../time-and-billing.service';
import { LinkPickerComponent } from './link-picker/link-picker.component';
import { MissingInfoSelectComponent } from 'src/app/components/missing-info/select/select.component';
import { TodoSelectComponent } from 'src/app/components/todos/select/select.component';
import { TaskSelectComponent } from 'src/app/components/tasks/select/select.component';

// What a time entry can be linked to (only one): a project, one of its tasks, missing info, a to do or an appointment
export type LinkType = 'none' | 'project' | 'task' | 'missing_info' | 'todo' | 'appointment';

@Component({
  selector: 'app-create',
  templateUrl: './create.page.html',
  styleUrls: ['./create.page.scss'],
})
export class CreatePage implements OnInit, DoCheck {

  @Input() project: any = null;
  @Input() task: any = null;
  // A to do to log time against (the entry is linked to it and takes its title and contact / company)
  @Input() todo: any = null;
  // Same for a missing information item
  @Input() missing_info: any = null;
  @Input() initial_start_time: Date = null;
  @Input() initial_end_time: Date = null;
  // Set when created from the timer: the time it counted, which can be less than start to end (it was paused)
  @Input() initial_worked_ms: number | null = null;
  @Input() reset_timer_on_save = false;
  // An existing time entry to show: the page is then the same form, view only (with Delete) instead of creating
  @Input() entry: any = null;

  // Viewing a saved entry: the form is locked until Edit is pressed (edit_mode), which unlocks it to update the entry
  edit_mode = false;

  get view_only(): boolean {
    return !!this.entry && !this.edit_mode;
  }

  current_user: any = null;
  time_and_billing = {
    user_guid: '0000',
    title: '',
    date: null as Date | null,
    start_time: null as Date | null,
    end_time: null as Date | null,
    hours: 0,
    minutes: 0,
    billing_group_guid: 0,
    status: 0,
    billable: false,
    project: null as any,
    client: null as any,
    company: null as any,
    tags: [],
    project_guid: '',
    missing_info_guid: '',
    todo_guid: '',
    appointment_guid: '',
    client_guid: '',
    company_guid: '',
    task_guid: '' as string | null,
    task_title: '' as string | null,
    description_internal: '',
    description_client: '',
    time_status: 'WIP',
    work_code_guid: '',
    billing_rate_per_hour: null as number | null,
    bill_to_diffferent_user: false,
    bill_to_different_user_guid: '00000',
    bill_by_time: true,
    billing_rate_guid: ''
  }
  team_members: any;
  time_is_correct = true;
  // When ticked the hours / minutes are typed in ("between the start and end time I worked ...") instead of
  // being the whole gap between the start and end time
  // The client description is hidden for now - set to true to bring the field back
  readonly show_client_description = false;

  // Same statuses as the time tracking table's bulk "Set Time Status"

  // Every billing rate the firm has set up (Manage Firm > Billing). A rate with no user_guid applies to all team members.
  all_billing_rates: any[] = [];
  billing_rates_loaded = false;

  // ── Linked to (project, task, missing info, to do or appointment - one only) ──
  link_type: LinkType = 'none';
  link_item: any = null; // the chosen task / missing info / to do / appointment (a project is time_and_billing.project)
  readonly link_type_options: { value: LinkType, label: string }[] = [
    { value: 'none', label: 'Not linked' },
    { value: 'project', label: 'Project' },
    { value: 'task', label: 'Task' },
    { value: 'missing_info', label: 'Missing Information' },
    { value: 'todo', label: 'To Do' },
    { value: 'appointment', label: 'Appointment' },
  ];

  // Quick search for the contact / company (same as the Missing Info page)
  client_data: any[] = [];
  company_data: any[] = [];
  combined_data: any[] = [];
  assigned_to_search_query = '';
  assigned_to_suggestions: any[] = [];
  assigned_to_suggestions_active_index = -1;
  show_assigned_to_suggestions = false;
  assigned_to_search_blurred_with_text = false;

  // Opened from a project or task: its client is pre-filled with the rest of the assignment and can't be changed
  get client_locked(): boolean {
    return !!(this.project || this.task) && !this.entry && (this.client_from_project || this.company_from_project) && this.HasAssignedEntity();
  }

  // A client that came from what the entry is linked to (project, task, to do, ...) can't be removed on its own - the link has to
  // be removed first, which takes the client with it
  get client_removal_blocked(): boolean {
    return !this.entry && (this.client_from_project || this.company_from_project) && this.HasAssignedEntity();
  }

  private client_from_project = false;
  private company_from_project = false;

  constructor(private apiService: ApiService, private modalController: ModalController, public globalService: GlobalService, private alertController: AlertController, private timeAndBillingService: TimeAndBillingService, private teamMemberAccessService: TeamMemberAccessService) { }

  ngOnInit() {
    this.current_user = this.globalService.GetCurrentUser();
    if (this.current_user?.guid) this.time_and_billing.user_guid = this.current_user.guid;

    if (this.entry) {
      this.LoadFromEntry();
      this.GetTeamMembers();
      this.GetBillingRates();
      this.GetWorkCodes();
      return;
    }

    // Time for a project starts with the project's title; for a task, "Project Name - Task Name"
    if (this.project != null) {
      this.time_and_billing.project = this.project;
      this.time_and_billing.project_guid = this.project.guid;
      if (!this.time_and_billing.title) this.time_and_billing.title = this.project.title || '';
      this.LinkClientFromItem(this.project.client_guid, this.project.company_guid, this.project.client?.display_name || this.project.company?.name || this.project.client_name || this.project.company_name || this.project.client_entity_name || '', this.project);
    }
    if (this.task != null) {
      this.time_and_billing.task_guid = this.task.guid;
      this.time_and_billing.task_title = this.task.title;
      const project_title = this.project?.title || this.task.project_title || '';
      this.time_and_billing.title = project_title ? `${project_title} - ${this.task.title || ''}` : (this.task.title || '');
    }
    if (this.todo != null) {
      this.link_type = 'todo';
      this.link_item = this.todo;
      this.time_and_billing.todo_guid = this.todo.guid;
      this.time_and_billing.title = this.todo.title || '';
      this.auto_title = this.time_and_billing.title;
      this.LinkClientFromItem(this.todo.client_guid, this.todo.company_guid, this.todo.linked_name || '');
    }
    if (this.missing_info != null) {
      this.link_type = 'missing_info';
      this.link_item = this.missing_info;
      this.time_and_billing.missing_info_guid = this.missing_info.guid;
      this.time_and_billing.title = this.missing_info.title || '';
      this.auto_title = this.time_and_billing.title;
      this.LinkClientFromItem(this.missing_info.client_guid, this.missing_info.company_guid, this.missing_info.client_entity_name || '');
    }
    // Start, end and the time worked always work together (see onTimeChange / OnWorkedChange), kept to the minute.
    // A new entry starts with the end one minute after the start. From the timer, the end is now and the start is the time
    // the timer counted before it - the time it was paused isn't part of the time worked.
    const currentTime = this.DropSeconds(new Date());
    let end_time = this.initial_end_time ? this.DropSeconds(this.initial_end_time) : null;
    let start_time = this.initial_start_time ? this.DropSeconds(this.initial_start_time) : null;
    if (this.initial_worked_ms != null && this.initial_worked_ms > 0) {
      end_time = end_time || currentTime;
      start_time = new Date(end_time.getTime() - Math.max(1, Math.round(this.initial_worked_ms / 60000)) * 60000);
    } else if (start_time && end_time && end_time.getTime() <= start_time.getTime()) {
      end_time = new Date(start_time.getTime() + 60000);
    } else if (!start_time && !end_time) {
      start_time = currentTime;
      end_time = new Date(currentTime.getTime() + 60000);
    } else if (!end_time) {
      end_time = new Date(start_time.getTime() + 60000);
    } else if (!start_time) {
      start_time = new Date(end_time.getTime() - 60000);
    }
    this.time_and_billing.date = start_time;
    this.time_and_billing.start_time = start_time;
    this.time_and_billing.end_time = end_time;
    this.onTimeChange();
    this.GetTeamMembers();
    this.GetCompaniesAndClients();
    this.GetBillingRates();
    this.GetWorkCodes();
  }

  // Fills the form from a saved entry (as returned by TimeAndBillingService, with project / client / company objects)
  private LoadFromEntry() {
    const e = this.entry;
    const tb = this.time_and_billing;
    tb.title = e.title || '';
    tb.user_guid = e.user_guid || '';
    tb.start_time = new Date(e.start_date);
    tb.end_time = new Date(e.end_date);
    tb.date = tb.start_time;
    tb.hours = Number(e.hours) || 0;
    tb.minutes = Number(e.minutes) || 0;
    tb.billable = !!e.billable;
    tb.billing_rate_per_hour = e.billing_rate_per_hour != null ? Number(e.billing_rate_per_hour) : null;
    tb.billing_rate_guid = e.billing_rate_guid || '';
    tb.time_status = e.time_status || 'WIP';
    tb.work_code_guid = e.work_code_guid || '';
    tb.description_internal = e.description_internal || '';
    tb.description_client = e.description_client || '';
    // A copy, so adding / removing tags while editing doesn't change the saved entry until it is saved
    tb.tags = (e.tags || []).map((tag: any) => ({ ...tag }));
    tb.project = e.project || null;
    tb.project_guid = e.project_guid || '';
    tb.client = e.client || null;
    tb.client_guid = e.client_guid || '';
    tb.company = e.company || null;
    tb.company_guid = e.company_guid || '';
    tb.task_guid = e.task_guid || '';
    tb.task_title = e.task_title || '';
    tb.missing_info_guid = e.missing_info_guid || '';
    tb.todo_guid = e.todo_guid || '';
    tb.appointment_guid = e.appointment_guid || '';

    // The hours and minutes are the time worked (which can be less than start to end), so show them as saved
    this.time_is_correct = true;

    if (e.linked) {
      this.link_type = e.linked.type;
      if (e.linked.type !== 'project') this.link_item = { guid: e.linked.type === 'task' ? e.task_guid : e.linked.guid, title: e.linked.type === 'task' ? e.task_title : e.linked.title };
    }
  }

  // The firm's work codes, which an entry can be given on its own or alongside whatever it is linked to
  work_codes: any[] = [];

  async GetWorkCodes() {
    if (!this.globalService.CanViewBillingInfo) return;
    const data: any = await this.apiService.get('billing/work-codes');
    this.work_codes = data?.success ? (data.data || []) : [];
  }

  // The codes that can be picked: the active ones, plus the one an entry already has even if it has since been switched off
  get work_code_options(): any[] {
    const chosen = this.time_and_billing.work_code_guid;
    return this.work_codes.filter((c: any) => c.active == 1 || c.active === true || c.guid === chosen);
  }

  async GetBillingRates() {
    const data: any = await this.apiService.get('billing/rates');
    this.all_billing_rates = data?.success ? (data.data || []) : [];
    this.billing_rates_loaded = true;
    this.CheckRateStillAvailable();
  }

  // Billable can only be set once a team member is chosen - the rates available depend on them.
  // A getter, so it follows time_and_billing.user_guid whenever that changes.
  get can_set_billable(): boolean {
    return !!this.time_and_billing.user_guid && this.time_and_billing.user_guid !== '0000';
  }

  // The active billing rates for the selected team member: the ones set up for them plus the ones for everybody
  get member_billing_rates(): any[] {
    const member = this.time_and_billing.user_guid;
    if (!this.can_set_billable) return [];
    return this.all_billing_rates.filter((rate: any) =>
      (rate.active == 1 || rate.active === true) && (!rate.user_guid || rate.user_guid === member));
  }

  // The rates for the custom dropdown, which shows each rate's colour and description
  // What will be billed: the time worked (hours and minutes) at the selected rate
  get billing_total(): number {
    const rate = Number(this.time_and_billing.billing_rate_per_hour) || 0;
    return rate > 0 ? (this.billed_minutes / 60) * rate : 0;
  }

  // The billing rate chosen for this entry (looked up in all the rates, so a saved entry still finds a rate that has since been switched off)
  get selected_rate(): any {
    const guid = this.time_and_billing.billing_rate_guid;
    return guid ? this.all_billing_rates.find((r: any) => r.guid === guid) || null : null;
  }

  // How the selected rate rounds: 2 = up, 3 = down, 4 = to the nearest
  get rounding_type(): number {
    return Number(this.selected_rate?.rounded_type) || 4;
  }

  // The rounding interval (minutes) of the selected rate, null when it doesn't round
  get rounding_interval(): number | null {
    const rate = this.selected_rate;
    if (!rate || !(rate.rounded == 1 || rate.rounded === true)) return null;
    const interval = Number(rate.rounded_time) || 0;
    const type = Number(rate.rounded_type);
    return interval > 0 && type >= 2 && type <= 4 ? interval : null;
  }

  // The exact minutes worked (what staff entered) - never changed by rounding
  get worked_minutes(): number {
    return (Number(this.time_and_billing.hours) || 0) * 60 + (Number(this.time_and_billing.minutes) || 0);
  }

  // The rounded set, stored beside the exact time for reporting (the API works it out the same way when saving).
  // Round up: start goes down, end goes up. Round down: start goes up, end goes down. Nearest: both to the closest
  // increment. If less time than that was worked (a paused timer), the worked time is rounded the same way instead.
  get rounded_time(): { start: Date, end: Date, minutes: number, interval: number } | null {
    const interval = this.rounding_interval;
    if (!interval) return null;
    const ms = interval * 60000;
    // Time is tracked to the minute, so the seconds are ignored (10:00:30 is 10:00, not a reason to round up to 10:05)
    const start = Math.floor(new Date(this.time_and_billing.start_time).getTime() / 60000) * 60000;
    const end = Math.floor(new Date(this.time_and_billing.end_time).getTime() / 60000) * 60000;
    if (isNaN(start) || isNaN(end)) return null;
    const type = this.rounding_type;
    const round = (value: number, direction: number) => (direction < 0 ? Math.floor(value / ms) : direction > 0 ? Math.ceil(value / ms) : Math.round(value / ms)) * ms;
    const rounded_start = round(start, type === 2 ? -1 : type === 3 ? 1 : 0);
    let rounded_end = Math.max(round(end, type === 2 ? 1 : type === 3 ? -1 : 0), rounded_start);
    let minutes = Math.round((rounded_end - rounded_start) / 60000);
    const worked = this.worked_minutes;
    if (worked > 0 && (end - start) / 60000 !== worked) {
      const steps = worked / interval;
      minutes = (type === 2 ? Math.ceil(steps) : type === 3 ? Math.floor(steps) : Math.round(steps)) * interval;
      rounded_end = rounded_start + minutes * 60000;
    }
    return { start: new Date(rounded_start), end: new Date(rounded_end), minutes, interval };
  }

  // The minutes the amount is worked out from: the rounded total when the rate rounds, otherwise the exact time.
  // A saved entry uses the rounded total that was stored with it.
  get billed_minutes(): number {
    if (this.view_only && this.entry?.rounded) return (Number(this.entry.rounded_hours) || 0) * 60 + (Number(this.entry.rounded_minutes) || 0);
    return this.rounded_time?.minutes ?? this.worked_minutes;
  }

  get is_rounded(): boolean {
    return this.view_only && this.entry ? !!this.entry.rounded : !!this.rounded_time;
  }

  // The actual and rounded time of a saved entry, side by side. Viewing shows what was saved; while editing, the actual time
  // follows the form and the rounded time is what the rate's rounding would make of it.
  get time_summary(): { actual: { start: any, end: any, minutes: number }, rounded: { start: any, end: any, minutes: number } | null } | null {
    if (!this.entry) return null;
    if (this.view_only) {
      const e = this.entry;
      return {
        actual: { start: e.start_date, end: e.end_date, minutes: (Number(e.hours) || 0) * 60 + (Number(e.minutes) || 0) },
        rounded: e.rounded && e.rounded_start_date && e.rounded_end_date
          ? { start: e.rounded_start_date, end: e.rounded_end_date, minutes: (Number(e.rounded_hours) || 0) * 60 + (Number(e.rounded_minutes) || 0) }
          : null
      };
    }
    const rounded = this.rounded_time;
    return {
      actual: { start: this.time_and_billing.start_time, end: this.time_and_billing.end_time, minutes: this.worked_minutes },
      rounded: rounded ? { start: rounded.start, end: rounded.end, minutes: rounded.minutes } : null
    };
  }

  // "1 hr, 56 min"
  FormatLong(total: number): string {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? `${h} hr, ${m} min` : `${m} min`;
  }

  FormatMinutes(total: number): string {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
  }

  // Cached: the same array is returned until the team member or the rates change (a new array every change
  // detection pass would make the dropdown re-render and trip Angular's expression-changed check)
  private _rate_options: any[] = [];
  private _rate_options_key = '';
  get billing_rate_options(): any[] {
    const rates = this.member_billing_rates;
    const key = rates.map((r: any) => `${r.guid}|${r.code}|${r.rate}|${r.colour}|${r.description}`).join(';');
    if (key !== this._rate_options_key) {
      this._rate_options_key = key;
      this._rate_options = rates.map((rate: any) => ({
        value: rate.guid,
        label: `${rate.code} · $${rate.rate} / hr`,
        description: rate.description || '',
        color: rate.colour || '#6c757d',
      }));
    }
    return this._rate_options;
  }

  OnTeamMemberChange(guid: string) {
    this.time_and_billing.user_guid = guid || '';
    this.CheckRateStillAvailable();
  }

  // Changing the team member changes which rates are available - drop a rate (or billable) that no longer fits
  private CheckRateStillAvailable() {
    // A saved entry keeps its rate as it was (even if that rate has since been switched off) until it is edited
    if (this.view_only) return;
    if (!this.can_set_billable) this.time_and_billing.billable = false;
    const chosen = this.time_and_billing.billing_rate_guid;
    if (chosen && !this.member_billing_rates.some((rate: any) => rate.guid === chosen)) {
      this.time_and_billing.billing_rate_guid = '';
      this.time_and_billing.billing_rate_per_hour = null;
    }
  }

  OnBillingRateChange(guid: string) {
    const rate = this.member_billing_rates.find((r: any) => r.guid === guid);
    this.time_and_billing.billing_rate_guid = rate ? rate.guid : '';
    this.time_and_billing.billing_rate_per_hour = rate ? Number(rate.rate) : null;
  }

  async GetCompaniesAndClients() {
    const [clients, companies]: any[] = await Promise.all([this.apiService.GetClients(), this.apiService.GetCompanies()]);
    this.client_data = clients?.data || [];
    this.company_data = companies?.data || [];
    this.client_data.forEach((client: any) => client.is_company = false);
    this.company_data.forEach((company: any) => company.is_company = true);
    this.combined_data = [...this.client_data, ...this.company_data];
    this.FillInClientName();
  }

  // A client linked automatically (from a project, task, ...) may not have come with a name - take it from the lists, or ask
  // for it when it isn't in them (e.g. a contact who isn't a client)
  private async FillInClientName() {
    const client = this.time_and_billing.client;
    if (client?.guid && !client.display_name) {
      let found = this.client_data.find((c: any) => c.guid === client.guid);
      if (!found) {
        const data: any = await this.apiService.get(`clients/${client.guid}`);
        found = data?.success ? data.data?.[0] : null;
      }
      if (found && this.time_and_billing.client === client) this.time_and_billing.client = { ...client, ...found, display_name: found.display_name || found.name || '' };
    }
    const company = this.time_and_billing.company;
    if (company?.guid && !(company.name || company.display_name)) {
      let found = this.company_data.find((c: any) => c.guid === company.guid);
      if (!found) {
        const data: any = await this.apiService.get(`companies/${company.guid}`);
        found = data?.success ? data.data?.[0] : null;
      }
      if (found && this.time_and_billing.company === company) this.time_and_billing.company = { ...company, ...found, name: found.name || found.display_name || '', display_name: found.display_name || found.name || '' };
    }
  }

  HasAssignedEntity(): boolean {
    return !!this.time_and_billing.client || !!this.time_and_billing.company;
  }

  OnAssignedToSearchChange() {
    this.assigned_to_search_blurred_with_text = false;
    const q = (this.assigned_to_search_query || '').trim().toLowerCase();
    this.assigned_to_suggestions_active_index = -1;
    if (!q || !this.combined_data?.length) {
      this.assigned_to_suggestions = [];
      this.show_assigned_to_suggestions = false;
      return;
    }
    this.assigned_to_suggestions = this.combined_data.filter((item: any) => {
      if (item.is_company) return (item.name || '').toLowerCase().includes(q);
      return (item.display_name || '').toLowerCase().includes(q) || (item.email || '').toLowerCase().includes(q);
    }).slice(0, 15);
    this.show_assigned_to_suggestions = this.assigned_to_suggestions.length > 0;
  }

  OnAssignedToInputFocus() {
    this.assigned_to_search_blurred_with_text = false;
    if (this.assigned_to_search_query) this.OnAssignedToSearchChange();
  }

  OnAssignedToInputBlur() {
    this.show_assigned_to_suggestions = false;
    this.assigned_to_search_blurred_with_text = !!this.assigned_to_search_query?.trim();
  }

  OnAssignedToInputKeydown(event: KeyboardEvent) {
    if (!this.show_assigned_to_suggestions || this.assigned_to_suggestions.length === 0) {
      if (event.key === 'Enter' && this.assigned_to_search_query?.trim()) {
        event.preventDefault();
        this.assigned_to_search_blurred_with_text = true;
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.assigned_to_suggestions_active_index = Math.min(this.assigned_to_suggestions_active_index + 1, this.assigned_to_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.assigned_to_suggestions_active_index = Math.max(this.assigned_to_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.assigned_to_suggestions_active_index >= 0 ? this.assigned_to_suggestions_active_index : 0;
      this.SelectAssignedToSuggestion(this.assigned_to_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_assigned_to_suggestions = false;
    }
  }

  // A time entry is for one contact or one company, never both
  SelectAssignedToSuggestion(item: any) {
    if (!item) return;
    if (item.is_company) {
      this.time_and_billing.company = item;
      this.time_and_billing.company_guid = item.guid;
      this.time_and_billing.client = null;
      this.time_and_billing.client_guid = '';
    } else {
      this.time_and_billing.client = item;
      this.time_and_billing.client_guid = item.guid;
      this.time_and_billing.company = null;
      this.time_and_billing.company_guid = '';
    }
    this.client_from_project = false;
    this.company_from_project = false;
    this.assigned_to_search_query = '';
    this.assigned_to_suggestions = [];
    this.assigned_to_suggestions_active_index = -1;
    this.show_assigned_to_suggestions = false;
    this.assigned_to_search_blurred_with_text = false;
  }

  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    this.team_members = data.data;
  }



  async Delete() {
    const alert = await this.alertController.create({
      header: 'Delete Time Entry',
      message: `Are you sure you want to delete "${this.entry?.title || 'this time entry'}"? This action cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            const data: any = await this.timeAndBillingService.Delete(this.entry.guid);
            this.globalService.presentToastMessage(data.message, data.success);
            if (data.success) {
              this.timeAndBillingService.NotifyChanged();
              this.modalController.dismiss({ deleted: true });
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async Close() {
    // Viewing an entry has nothing to discard
    if (this.view_only) {
      this.modalController.dismiss(null);
      return;
    }
    const isDirty = this.time_and_billing.title?.trim()
      || this.time_and_billing.description_internal?.trim()
      || this.time_and_billing.description_client?.trim()
      || this.time_and_billing.project_guid;
    if (isDirty) {
      const alert = await this.alertController.create({
        header: 'Are you sure?',
        message: 'You have unsaved changes. If you cancel now, your work will be lost.',
        buttons: [
          { text: 'Keep Editing', role: 'cancel' },
          { text: 'Discard', role: 'destructive', handler: () => this.modalController.dismiss(null) }
        ]
      });
      await alert.present();
    } else {
      this.modalController.dismiss(null);
    }
  }

  // Why the entry can't be created / saved for the team member it is set to ('' when it can): creating time is a permission
  // for yourself (time_own_create) and another for other team members (time_others_create). Editing an entry only needs
  // that when it is handed to a different team member - the edit permission itself is checked when Edit is pressed.
  get save_permission_message(): string {
    const member = this.time_and_billing.user_guid;
    if (!member || member === '0000') return '';
    if (this.entry && member === this.entry.user_guid) return '';
    if (this.globalService.CanCreateTimeFor(member)) return '';
    return member === this.current_user?.guid
      ? "You don't have permission to create time entries for yourself."
      : "You don't have permission to create time entries for other team members.";
  }

  // ── Warning when the time is for a team member who can't see its contact / company ──
  // Checked whenever the team member or the assigned contact / company changes. It only warns - saving is never blocked.
  access_warning = '';
  private access_check_key = '';

  ngDoCheck() {
    // The team member list is part of the key so the check runs again once it has loaded (it holds who is an admin)
    const key = `${this.view_only ? 'view' : 'edit'}|${this.time_and_billing.user_guid || ''}|${this.time_and_billing.client_guid || ''}|${this.time_and_billing.company_guid || ''}|${this.team_members?.length || 0}`;
    if (key === this.access_check_key) return;
    this.access_check_key = key;
    this.CheckAssigneeAccess(key);
  }

  private async CheckAssigneeAccess(key: string) {
    this.access_warning = '';
    const member_guid = this.time_and_billing.user_guid;
    if (this.view_only || !member_guid || member_guid === '0000' || member_guid === this.current_user?.guid) return;
    if (!this.time_and_billing.client_guid && !this.time_and_billing.company_guid) return;

    const member = this.team_members?.find((m: any) => m.guid === member_guid);
    if (!member) return; // the team member list hasn't loaded yet - the check runs again when it has
    const result = await this.teamMemberAccessService.Check(member, this.time_and_billing.client_guid, this.time_and_billing.company_guid);
    // Something else changed while this was loading - that change has its own check
    if (key !== this.access_check_key || !result || result.has_access) return;
    const who = member.display_name || 'This team member';
    const client = this.time_and_billing.client?.display_name || this.time_and_billing.company?.name
      || this.time_and_billing.company?.display_name || 'this contact / company';
    this.access_warning = `${who} doesn't have access to ${client}, so they won't be able to see this time entry.`;
  }

  // Opens the project picker and resolves with the chosen project (or null)
  async PickProject(): Promise<any> {
    const modal = await this.modalController.create({
      component: ProjectSelectPage,
      cssClass: 'large-modal',
      componentProps: {
        max_allowed_selections: 1,
        client_guid: this.time_and_billing.client_guid || '',
        company_guid: this.time_and_billing.company_guid || '',
        hide_create_button: true
      },
    });
    await modal.present();
    const data: any = await modal.onDidDismiss();
    return data?.data && data.data.length > 0 ? data.data[0] : null;
  }

  ApplyProject(project: any) {
    this.time_and_billing.project = project;
    this.time_and_billing.project_guid = project.guid;

    // Auto-populate client/company from project if none selected yet
    this.LinkClientFromItem(project.client_guid, project.company_guid, project.client?.display_name || project.company?.name, project);
  }

  // When what the time is linked to (project, task, missing info, to do, appointment) belongs to a contact or
  // company, the time is linked to them too - unless somebody is already selected (the pickers only offer items
  // for that client or with nobody assigned, so they can't conflict). Removed again when the link is removed.
  private LinkClientFromItem(client_guid: string, company_guid: string, name = '', source: any = null) {
    if (this.HasAssignedEntity()) return;
    if (client_guid) {
      this.time_and_billing.client = (source?.client?.display_name ? source.client : null) || this.client_data.find((c: any) => c.guid === client_guid) || { guid: client_guid, display_name: name };
      this.time_and_billing.client_guid = client_guid;
      this.client_from_project = true;
    } else if (company_guid) {
      this.time_and_billing.company = ((source?.company?.name || source?.company?.display_name) ? source.company : null) || this.company_data.find((c: any) => c.guid === company_guid) || { guid: company_guid, name, display_name: name };
      this.time_and_billing.company_guid = company_guid;
      this.company_from_project = true;
    }
  }

  private RemoveAutoLinkedClient() {
    if (this.client_from_project) {
      this.time_and_billing.client = null;
      this.time_and_billing.client_guid = '';
      this.client_from_project = false;
    }
    if (this.company_from_project) {
      this.time_and_billing.company = null;
      this.time_and_billing.company_guid = '';
      this.company_from_project = false;
    }
  }

  async presentSelectProjectModal() {
    const project = await this.PickProject();
    if (project) this.ApplyProject(project);
  }

  // ── Linked to ──
  OnLinkTypeChange(type: LinkType) {
    if (type === this.link_type) return;
    this.ClearLink();
    this.link_type = type;
  }

  HasLink(): boolean {
    if (this.link_type === 'project') return !!this.time_and_billing.project;
    if (this.link_type === 'task') return !!this.link_item && !!this.time_and_billing.project;
    return !!this.link_item;
  }

  LinkTypeLabel(): string {
    return this.link_type_options.find(o => o.value === this.link_type)?.label || '';
  }

  // What the badge shows for the current link
  GetLinkTitle(): string {
    if (this.link_type === 'project') return this.time_and_billing.project?.title || '';
    if (this.link_type === 'task') return `${this.time_and_billing.project?.title || ''} > ${this.link_item?.title || ''}`;
    return this.link_item?.title || '';
  }

  ClearLink() {
    const was_project_link = this.link_type === 'project' || this.link_type === 'task';
    this.link_item = null;
    this.time_and_billing.task_guid = '';
    this.time_and_billing.task_title = '';
    this.time_and_billing.missing_info_guid = '';
    this.time_and_billing.todo_guid = '';
    this.time_and_billing.appointment_guid = '';
    if (was_project_link) this.RemoveProject(null);
    else this.RemoveAutoLinkedClient();
  }

  async OpenLinkPicker() {
    switch (this.link_type) {
      case 'project':
        return await this.presentSelectProjectModal();
      case 'task':
        return await this.PickTask();
      case 'missing_info':
        return await this.PickFromTable(MissingInfoSelectComponent);
      case 'todo':
        return await this.PickFromTable(TodoSelectComponent);
      case 'appointment':
        return await this.PickFromList('Select Appointment', 'No recent appointments found for this team member.', await this.GetAppointmentItems());
    }
  }

  // The tasks table in selection mode (like the Tasks page): pick the task, and its project and client come with it
  private async PickTask() {
    const modal = await this.modalController.create({
      component: TaskSelectComponent,
      cssClass: 'large-modal',
      componentProps: {
        client_guid: this.time_and_billing.client_guid || '',
        company_guid: this.time_and_billing.company_guid || '',
        client_name: this.time_and_billing.client?.display_name || this.time_and_billing.company?.name || this.time_and_billing.company?.display_name || '',
        max_allowed_selections: 1
      },
      backdropDismiss: false,
    });
    await modal.present();
    const data: any = await modal.onDidDismiss();
    const picked = data?.data;
    if (!picked?.task) return;
    if (picked.project) this.ApplyProject(picked.project);
    this.link_item = { guid: picked.task.guid, title: picked.task.title };
    this.time_and_billing.task_guid = picked.task.guid;
    this.time_and_billing.task_title = picked.task.title;
  }

  private auto_title = '';

  // The title of a linked missing info / to do / appointment becomes the entry title, unless the user typed their own
  private DefaultTitle(title: string) {
    if (!title) return;
    if (!this.time_and_billing.title || this.time_and_billing.title === this.auto_title) {
      this.time_and_billing.title = title;
      this.auto_title = title;
    }
  }

  // Shows the picker. When set_link is true the choice becomes the link, otherwise it is just returned
  private async PickFromList(title: string, empty_message: string, items: any[], set_link = true): Promise<any> {
    const modal = await this.modalController.create({
      component: LinkPickerComponent,
      cssClass: 'small-modal tall-modal secondary-modal',
      componentProps: { title, items, empty_message },
    });
    await modal.present();
    const data: any = await modal.onDidDismiss();
    const chosen = data?.data || null;
    if (chosen && set_link) {
      this.link_item = chosen;
      if (this.link_type === 'missing_info') this.time_and_billing.missing_info_guid = chosen.guid;
      else if (this.link_type === 'todo') this.time_and_billing.todo_guid = chosen.guid;
      else if (this.link_type === 'appointment') {
        this.time_and_billing.appointment_guid = chosen.guid;
        this.LinkClientFromItem(chosen.client_guid, '', chosen.client_name);
      }
      this.DefaultTitle(chosen.title);
    }
    return chosen;
  }

  // Shows a table in selection mode (max of one) and links the entry to the chosen row
  private async PickFromTable(component: any) {
    const modal = await this.modalController.create({
      component,
      cssClass: 'large-modal',
      componentProps: {
        max_allowed_selections: 1,
        // With a contact / company selected, only theirs (and items with nobody assigned) can be linked
        client_guid: this.time_and_billing.client_guid || '',
        company_guid: this.time_and_billing.company_guid || '',
        client_name: this.time_and_billing.client?.display_name || this.time_and_billing.company?.name || this.time_and_billing.company?.display_name || ''
      },
      backdropDismiss: false,
    });
    await modal.present();
    const data: any = await modal.onDidDismiss();
    const chosen = data?.data || null;
    if (!chosen) return;
    this.link_item = chosen;
    if (this.link_type === 'missing_info') this.time_and_billing.missing_info_guid = chosen.guid;
    else if (this.link_type === 'todo') this.time_and_billing.todo_guid = chosen.guid;
    this.DefaultTitle(chosen.title);
    // Missing info rows carry client_entity_name, to do rows linked_name
    this.LinkClientFromItem(chosen.client_guid, chosen.company_guid, chosen.client_entity_name || chosen.linked_name || '');
  }

  // With a contact / company selected only their appointments, or ones with no client attending, can be linked.
  // Clients are the attendees with type 2 (team members are type 1); companies have no attendees of their own.
  private GetAppointmentClients(event: any): any[] {
    let attendees: any = event.users || [];
    if (typeof attendees === 'string') { try { attendees = JSON.parse(attendees); } catch { attendees = []; } }
    return (attendees as any[]).filter(u => u.type == 2);
  }

  // The client attending an appointment, if there is one
  private GetAppointmentClient(event: any): any {
    return this.GetAppointmentClients(event)[0] || null;
  }

  private AppointmentMatchesClient(event: any): boolean {
    const client_guid = this.time_and_billing.client_guid;
    const company_guid = this.time_and_billing.company_guid;
    if (!client_guid && !company_guid) return true;
    const clients = this.GetAppointmentClients(event);
    if (!clients.length) return true;
    return !!client_guid && clients.some(u => u.guid === client_guid);
  }

  // The selected team member's appointments from the last 60 days and the next 14
  private async GetAppointmentItems(): Promise<any[]> {
    const start = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
    const end = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    const data: any = await this.apiService.get(
      `calendar/events?team_member=${this.time_and_billing.user_guid || ''}&start_time=${start.toISOString()}&end_time=${end.toISOString()}`);
    const events: any[] = (data?.success ? (data.data || []) : []).filter((e: any) => e.type == 1 && this.AppointmentMatchesClient(e)); // real appointments only - not the calendar's assigned work (tasks) or blocked time
    return events
      .sort((a: any, b: any) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
      .map((e: any) => {
        const client = this.GetAppointmentClient(e);
        return { guid: e.guid, title: e.title, subtitle: e.start_time ? new Date(e.start_time).toLocaleString() : '', client_guid: client?.guid || '', client_name: client?.display_name || '' };
      });
  }

  async presentSelectClientModal() {
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_client_selection: true,
        allow_non_client_selection: false,
        allow_company_selection: true,
        allow_contact_selection: true,
        allow_company_group_selection: false,
        allow_contact_group_selection: false,
        max_allowed_selections: 1,
        expanded_clients: true
      },
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.length > 0) {
        const element = data.data[0];
        if (element.is_company) {
          this.time_and_billing.company = element;
          this.time_and_billing.company_guid = element.guid;
          this.time_and_billing.client = null;
          this.time_and_billing.client_guid = '';
        } else if (element.is_contact) {
          this.time_and_billing.client = element;
          this.time_and_billing.client_guid = element.guid;
          this.time_and_billing.company = null;
          this.time_and_billing.company_guid = '';
        }
        this.client_from_project = false;
        this.company_from_project = false;
      }
    });
    return await modal.present();
  }

  // A copy of the date with the seconds and milliseconds removed (rounded down to the minute)
  private DropSeconds(value: any): Date {
    const date = new Date(value);
    date.setSeconds(0, 0);
    return date;
  }

  // The start time, end time and the hours / minutes worked always agree. Changing the start or end time works out the hours /
  // minutes; changing the hours / minutes moves the end time.
  async onTimeChange() {
    if (this.time_and_billing.start_time && this.time_and_billing.end_time) {
      const start = this.DropSeconds(this.time_and_billing.start_time).getTime();
      const end = this.DropSeconds(this.time_and_billing.end_time).getTime();
      if (end > start) {
        this.time_is_correct = true;
        const minutes = Math.round((end - start) / 60000);
        this.time_and_billing.hours = Math.floor(minutes / 60);
        this.time_and_billing.minutes = minutes % 60;
      }
      else {
        this.time_is_correct = false;
      }
    }
  }

  // The hours or minutes were changed: the end time follows (start time + the time worked)
  OnWorkedChange() {
    const minutes = (Number(this.time_and_billing.hours) || 0) * 60 + (Number(this.time_and_billing.minutes) || 0);
    if (!this.time_and_billing.start_time) return;
    if (minutes <= 0) {
      this.time_is_correct = false;
      return;
    }
    this.time_is_correct = true;
    this.time_and_billing.end_time = new Date(this.DropSeconds(this.time_and_billing.start_time).getTime() + minutes * 60000);
  }

  async RemoveCompany(company) {
    this.time_and_billing.company = null;
    this.time_and_billing.company_guid = '';
    this.company_from_project = false;
  }
  async RemoveProject(project) {
    this.time_and_billing.project = null;
    this.time_and_billing.project_guid = '';
    if (this.client_from_project) {
      this.time_and_billing.client = null;
      this.time_and_billing.client_guid = '';
      this.client_from_project = false;
    }
    if (this.company_from_project) {
      this.time_and_billing.company = null;
      this.time_and_billing.company_guid = '';
      this.company_from_project = false;
    }
  }
  async RemoveClient(client) {
    this.time_and_billing.client = null;
    this.time_and_billing.client_guid = '';
    this.client_from_project = false;
  }

  // ── Editing a saved entry ──
  StartEdit() {
    this.edit_mode = true;
  }

  // Throws away the changes made while editing and goes back to the saved entry
  CancelEdit() {
    this.edit_mode = false;
    this.ResetFormToEntry();
  }

  private ResetFormToEntry() {
    this.link_type = 'none';
    this.link_item = null;
    this.client_from_project = false;
    this.company_from_project = false;
    this.LoadFromEntry();
  }

  async Update() {
    const entry = this.BuildEntryPayload();
    if (!entry) return;

    const data: any = await this.timeAndBillingService.Update(this.entry.guid, entry);
    this.globalService.presentToastMessage(data.message, data.success);
    if (!data.success) return;

    // Back to viewing, now showing what was saved
    this.entry = data.data;
    this.edit_mode = false;
    this.ResetFormToEntry();
    // Any visible time entries (the time tracking page, a project or task's time tab, the calendar) reload
    this.timeAndBillingService.NotifyChanged();
  }

  async Create() {
    const entry = this.BuildEntryPayload();
    if (!entry) return;

    const data: any = await this.timeAndBillingService.Create(entry);
    this.globalService.presentToastMessage(data.message, data.success);
    if (!data.success) return;

    if (this.reset_timer_on_save) await this.globalService.ResetTimer();
    // Any visible time entries (the time tracking page, a project or task's time tab, the calendar) reload
    this.timeAndBillingService.NotifyChanged();
    this.modalController.dismiss({ success: true });
  }

  // Checks the form and builds what the API takes to create or update an entry; null (with a message) when something is missing
  private BuildEntryPayload(): any | null {
    if (!this.time_and_billing.title?.trim()) {
      this.globalService.presentToastMessage('A title is required to save a time entry.', false);
      return null;
    }
    if (this.link_type !== 'none' && !this.HasLink()) {
      this.globalService.presentToastMessage(`Select the ${this.LinkTypeLabel().toLowerCase()} this time is linked to, or choose "Not linked".`, false);
      return null;
    }
    if (!this.HasAssignedEntity()) {
      this.globalService.presentToastMessage('A client (contact or company) is required to save a time entry.', false);
      return null;
    }
    if (this.time_and_billing.billable && !(this.time_and_billing.billing_rate_per_hour > 0)) {
      this.globalService.presentToastMessage('A billing rate is required for billable entries.', false);
      return null;
    }
    if (this.time_and_billing.project) this.time_and_billing.project_guid = this.time_and_billing.project.guid;
    if (this.task) { this.time_and_billing.task_guid = this.task.guid; this.time_and_billing.task_title = this.task.title; }

    const startDate = this.DropSeconds(this.time_and_billing.start_time);
    const endDate = this.DropSeconds(this.time_and_billing.end_time);

    // The API only needs guids for project / client / company, not the nested objects the form holds
    const { project, client, company, date, start_time, end_time, bill_to_diffferent_user, ...fields } = this.time_and_billing;
    return {
      ...fields,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      bill_to_different_user: bill_to_diffferent_user,
      tags: this.time_and_billing.tags || []
    };
  }


  async presentSelectTagsModal() {
    let tags_copy = JSON.stringify(this.time_and_billing.tags);
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'time_and_billing',
        selected_tags: this.time_and_billing.tags
      }
    })
    modal.onDidDismiss().then((data: any) => {

      if (data && data.data) {
        tags_copy = JSON.parse(tags_copy);
      }
      else {
        this.time_and_billing.tags = [...JSON.parse(tags_copy)];
      }
    });
    return await modal.present()
  }
  async RemoveTag(tag) {
    this.globalService.RemoveFromArray(this.time_and_billing.tags, tag.guid);
  }



}
